import { supabase } from '../client/browser'
import { AuthError, User, Session } from '@supabase/supabase-js'
import { consentService } from './consent'

/**
 * Authentication service - simplified best practices
 * Let Supabase handle the complexity
 */
export const authService = {
  /**
   * Get current session
   */
  async getSession() {
    return supabase.auth.getSession()
  },

  /**
   * Get current user
   */
  async getUser() {
    return supabase.auth.getUser()
  },

  /**
   * Send OTP code to email (passwordless)
   * Better than magic links for cross-browser support
   * NOTE: Only for existing users - new users must use signUp flow
   */
  async sendOtpCode(email: string) {
    console.log('[Auth] Sending OTP code to:', email);

    // Send OTP without redirect URL to get a code instead of a link
    const { data, error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: false, // Only allow existing users to sign in via OTP
        // Note: When emailRedirectTo is not provided, Supabase sends a code instead of a link
        // Codes are 6 digits and expire after 10 minutes
      }
    })

    if (error) {
      console.error('[Auth] OTP error:', error);

      // Check for rate limiting error
      if (error.message?.includes('rate') || error.message?.includes('too many')) {
        return {
          data,
          error: new Error('Please wait 60 seconds before requesting another code.')
        }
      }

      // Map Supabase's "no signup allowed" + "user not found" variants to a
      // single friendly message. Downstream callers (booking AuthClient,
      // LoginForm) match on "No account found" and redirect to /signup.
      const msg = error.message?.toLowerCase() ?? ''
      if (
        msg.includes('user not found') ||
        msg.includes('not found') ||
        msg.includes('signups not allowed') ||
        msg.includes('signups disabled')
      ) {
        return {
          data,
          error: new Error('No account found with this email. Please sign up first.')
        }
      }
    }

    return { data, error }
  },

  /**
   * Verify OTP code and sign in
   */
  async verifyOtpCode(email: string, token: string) {
    console.log('[Auth] Verifying OTP code for:', email);
    
    const { data, error } = await supabase.auth.verifyOtp({
      email,
      token,
      type: 'email'
    })
    
    if (error) {
      console.error('[Auth] OTP verification error:', error);
      
      if (error.message?.includes('expired')) {
        return {
          data,
          error: new Error('This code has expired. Please request a new one.')
        }
      } else if (error.message?.includes('invalid')) {
        return {
          data,
          error: new Error('Invalid code. Please check and try again.')
        }
      }
    }
    
    return { data, error }
  },

  /**
   * DEPRECATED: forwards to sendOtpCode. Only existing accounts can sign
   * in this way — the Supabase project has "Allow signups via OTP" off,
   * so shouldCreateUser must stay false.
   */
  async signInWithMagicLink(
    email: string,
    _redirectPath?: string,
    _options?: { appType?: 'client' | 'therapist' }
  ) {
    return this.sendOtpCode(email)
  },

  /**
   * Sign in with email and password
   */
  async signIn(email: string, password: string, options?: { rememberMe?: boolean }) {
    const rememberMe = options?.rememberMe ?? true
    
    // If rememberMe is false, we'll store the session with a shorter expiry
    // and clear it on browser close
    if (!rememberMe && typeof window !== 'undefined') {
      // Store a flag to clear session on browser close
      sessionStorage.setItem('supabase-no-persist', 'true')
    }
    
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    })
    
    if (data.session && !rememberMe && typeof window !== 'undefined') {
      // Move auth data from localStorage to sessionStorage
      // This will clear when the browser is closed
      const keys = Object.keys(localStorage)
      keys.forEach(key => {
        if (key.startsWith('sb-') || key.includes('supabase')) {
          const value = localStorage.getItem(key)
          if (value && value.includes('access_token')) {
            localStorage.removeItem(key)
            sessionStorage.setItem(key, value)
          }
        }
      })
    }
    
    return { session: data.session, user: data.user, error }
  },

  /**
   * Sign up new user
   */
  async signUp(email: string, password: string, metadata?: Record<string, any>) {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          ...metadata,
          isClient: true // Mark this as a client user
        }
      }
    })
    
    if (error || !data.user) {
      return { session: data.session, user: data.user, error }
    }
    
    console.log('[AuthService] Auth signup successful, now creating user record for:', data.user.id);
    
    // Create user record in users table with same ID as auth
    // First check if user already exists
    const { data: existingUser, error: checkError } = await supabase
      .from('users')
      .select('id')
      .eq('id', data.user.id)
      .maybeSingle() // Use maybeSingle instead of single to avoid error when no rows
    
    console.log('[AuthService] Check for existing user result:', { existingUser, checkError });
    
    if (checkError) {
      console.error('[AuthService] Error checking for existing user:', checkError)
    }
    
    if (!existingUser) {
      console.log('[AuthService] No existing user found, creating new user record for:', data.user.id)
      // Upsert: the handle_new_auth_user DB trigger may have already inserted
      // a stub row when auth.signUp ran. Upsert overwrites it with the real
      // names/marketing prefs rather than PK-colliding.
      const userRecord = {
        id: data.user.id, // Use same ID as auth user
        email: email,
        firstName: metadata?.firstName || 'User',
        timezone: metadata?.timezone || null,
        marketingOptIn: metadata?.marketingOptIn === true,
        newsletterOptIn: metadata?.newsletterOptIn === true,
        marketingOptInDate: metadata?.marketingOptInDate || null,
        isPaidReferrer: metadata?.isPaidReferrer || false,
        referralSource: metadata?.referralSource || null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }

      console.log('Attempting to upsert user record:', userRecord)

      const { data: insertedUser, error: userError } = await supabase
        .from('users')
        .upsert(userRecord, { onConflict: 'id' })
        .select()
        .single()
      
      if (userError) {
        console.error('Error creating user record:', userError)
        console.error('User record that failed:', userRecord)
        console.error('Error details:', {
          code: userError.code,
          message: userError.message,
          details: userError.details,
          hint: userError.hint
        })
        
        // User record creation failed - this is critical
        // Return an error so the signup form knows to handle this
        return { 
          session: data.session, 
          user: data.user, 
          error: new Error('Failed to create user profile. Please contact support.') 
        }
      } else {
        console.log('User record created successfully:', insertedUser)
        
        // Save consent records for terms and privacy (always accepted during signup)
        // Marketing preferences are stored in the user table
        try {
          const consentsResult = await consentService.saveSignupConsents(
            data.user.id,
            metadata?.ipAddress
          )
          
          if (!consentsResult) {
            console.error('Failed to save consent records, but user was created')
          } else {
            console.log('Consent records saved successfully')
          }
        } catch (consentError) {
          console.error('Error saving consent records:', consentError)
          // Don't fail the signup if consent saving fails
        }
      }
    } else {
      // Update existing user record with marketing preferences
      console.log('Updating existing user record for:', data.user.id)
      const { error: updateError } = await supabase
        .from('users')
        .update({
          marketingOptIn: metadata?.marketingOptIn === true,
          newsletterOptIn: metadata?.newsletterOptIn === true,
          marketingOptInDate: metadata?.marketingOptInDate || null,
          updatedAt: new Date().toISOString()
        })
        .eq('id', data.user.id)
      
      if (updateError) {
        console.error('Error updating user record:', updateError)
      }
    }
    
    return { session: data.session, user: data.user, error }
  },

  /**
   * Sign out current user
   */
  async signOut() {
    try {
      console.log('[authService] Starting signOut...')
      // Sign out from Supabase
      const { error } = await supabase.auth.signOut()
      console.log('[authService] Supabase signOut completed, error:', error)
      
      if (error) {
        console.error('[authService] Supabase signOut error:', error)
        return { error }
      }
      
      // Manually clear the auth storage to ensure complete logout
      if (typeof window !== 'undefined') {
        // Clear all Supabase-related storage
        const keys = Object.keys(localStorage)
        keys.forEach(key => {
          if (key.startsWith('sb-') || key.includes('supabase')) {
            localStorage.removeItem(key)
          }
        })
        
        // Clear session storage
        sessionStorage.clear()
      }
      
      return { error: null }
    } catch (err) {
      return { error: err as Error }
    }
  },

  /**
   * Send password reset email
   */
  async resetPassword(email: string, options?: { appType?: 'client' | 'therapist' }) {
    // Check if user is in 25fall cohort (only for therapist app)
    // Commented out per user request
    // if (options?.appType === 'therapist' || !options?.appType) {
    //   const { data: profileData, error: profileError } = await supabase
    //     .from('pros')
    //     .select('cohort')
    //     .eq('email', email)
    //     .maybeSingle()
    //   
    //   if (profileError) {
    //     console.error('[Auth] Error checking cohort:', profileError)
    //   }
    //   
    //   // If user is in 25fall cohort, return error
    //   if (profileData?.cohort === '25fall') {
    //     return { 
    //       data: null, 
    //       error: new Error('Users in the 25fall cohort do not have email access yet and cannot reset their password at this time.')
    //     }
    //   }
    // }
    
    // Determine the correct redirect URL based on app type
    let redirectUrl = window.location.origin;
    const appType = options?.appType || 'therapist'; // Default to therapist for backward compatibility
    
    // In production, use the correct app URL
    if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
      if (appType === 'therapist') {
        // Use therapist app domain
        redirectUrl = process.env.NEXT_PUBLIC_THERAPIST_APP_URL || 
                     process.env.NEXT_PUBLIC_APP_URL || 
                     'https://app.alli.io';
      } else if (appType === 'client') {
        // Use the current domain to maintain session consistency
        const hostname = window.location.hostname;
        if (hostname === 'alli.io' || hostname === 'www.alli.io') {
          redirectUrl = `${window.location.protocol}//${hostname}`;
        } else {
          // Fallback for other domains
          redirectUrl = process.env.NEXT_PUBLIC_CLIENT_APP_URL || `${window.location.protocol}//${hostname}`;
        }
      }
    } else {
      // Local development - use the appropriate port
      const localPort = appType === 'therapist' ? '3001' : '3002';
      redirectUrl = `${window.location.protocol}//${window.location.hostname}:${localPort}`;
    }
    
    // Route through /auth/callback with next=/reset-password
    // Supabase only allows redirect URLs listed in the dashboard,
    // and /auth/callback is already allowed. The callback page will
    // detect the password recovery flow and redirect to /reset-password.
    const callbackUrl = `${redirectUrl}/auth/callback?next=/reset-password`;

    // Deliberately origin + pathname only (no href) — the reset-password
    // callback lands here with a recovery token in the URL fragment; a
    // full-href console.log would print live credentials.
    console.log('[Auth] Reset password called from:', window.location.origin + window.location.pathname);
    console.log('[Auth] App type:', appType);
    console.log('[Auth] Password reset redirect URL:', callbackUrl);

    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: callbackUrl
    })
    return { data, error }
  },

  /**
   * Update user password
   */
  async updatePassword(newPassword: string) {
    try {
      const { data, error } = await supabase.auth.updateUser({
        password: newPassword
      })
      return { user: data.user, error }
    } catch (err) {
      return { 
        user: null, 
        error: err as AuthError 
      }
    }
  },

  /**
   * Refresh the session (usually not needed with autoRefreshToken: true)
   */
  async refreshSession() {
    return supabase.auth.refreshSession()
  },
  
  /**
   * Subscribe to auth state changes
   */
  onAuthStateChange(callback: Parameters<typeof supabase.auth.onAuthStateChange>[0]) {
    return supabase.auth.onAuthStateChange(callback)
  }
}